import { MathUtils } from "three";
import { AnatomicalSideEnum, BoneTypeEnum, ReferenceTypeEnum } from "../Models/CaseModel.js";
import { Utils } from "./Utils.js";
import { RingTypeEnum } from "../Models/RingModel.js";
// TODO: improve : { [key in RingTypeEnum]?: number }
const a = MathUtils.degToRad;
export class AxialRotations {
    static getReference(boneType, refType, side, reference, secondary) {
        this.init();
        const proximal = refType == ReferenceTypeEnum.Proximal ? reference : secondary;
        const distal = refType == ReferenceTypeEnum.Proximal ? secondary : reference;
        return this._axialRotations[boneType][refType][side][proximal][distal];
    }
    static getSecondary(boneType, refType, side, reference, secondary) {
        this.init();
        const oppositeRefType = refType === ReferenceTypeEnum.Proximal ? ReferenceTypeEnum.Distal : ReferenceTypeEnum.Proximal;
        const proximal = refType == ReferenceTypeEnum.Proximal ? reference : secondary;
        const distal = refType == ReferenceTypeEnum.Proximal ? secondary : reference;
        return this.getSecondarySpecialCase(refType, side, proximal, distal) ?? this._axialRotations[boneType][oppositeRefType][side][proximal][distal];
    }
    static getSecondarySpecialCase(refType, side, proximal, distal) {
        const internal = 1, external = -1;
        if (refType === ReferenceTypeEnum.Proximal && side === AnatomicalSideEnum.Left) {
            if (proximal === RingTypeEnum.OpenMedialRing && distal === RingTypeEnum.OpenAnteriorRing)
                return a(120) * external;
        }
        if (refType === ReferenceTypeEnum.Proximal && side === AnatomicalSideEnum.Right) {
            if (proximal === RingTypeEnum.OpenPosteriorRing && distal === RingTypeEnum.OpenMedialRing)
                return a(30) * external;
        }
        if (refType === ReferenceTypeEnum.Distal && side === AnatomicalSideEnum.Left) {
            if (proximal === RingTypeEnum.OpenAnteriorRing && distal === RingTypeEnum.OpenMedialRing)
                return a(60) * external;
            if (proximal === RingTypeEnum.OpenMedialRing && distal === RingTypeEnum.OpenAnteriorRing)
                return a(30) * internal;
        }
        if (refType === ReferenceTypeEnum.Distal && side === AnatomicalSideEnum.Right) {
            if (proximal === RingTypeEnum.OpenPosteriorRing && distal === RingTypeEnum.OpenMedialRing)
                return a(120) * internal;
        }
        return undefined;
    }
    static init() {
        if (!this._axialRotations) {
            const internal = 1, external = -1;
            this._axialRotations = {};
            this._axialRotations[BoneTypeEnum.LongLeg] = { Proximal: { Left: {}, Right: {} }, Distal: { Left: {}, Right: {} } };
            this._axialRotations[BoneTypeEnum.Femur] = this._axialRotations[BoneTypeEnum.LongLeg];
            this._axialRotations[BoneTypeEnum.Tibia] = this._axialRotations[BoneTypeEnum.LongLeg];
            //#region LONGLEG FEMUR TIBIA - PROXIMAL LEFT
            let fullRing = this._axialRotations[BoneTypeEnum.LongLeg].Proximal.Left[RingTypeEnum.FullRing] = {};
            fullRing[RingTypeEnum.FullRing] = 0;
            fullRing[RingTypeEnum.FullRing58Plus38] = 0;
            fullRing[RingTypeEnum.OpenAnteriorRing] = 0;
            fullRing[RingTypeEnum.OpenMedialRing] = a(30) * external;
            fullRing[RingTypeEnum.OpenPosteriorRing] = a(60) * external;
            fullRing[RingTypeEnum.FootPlate] = 0;
            this._axialRotations[BoneTypeEnum.LongLeg].Proximal.Left[RingTypeEnum.FullRing58Plus38] = Utils.clone(this._axialRotations[BoneTypeEnum.LongLeg].Proximal.Left[RingTypeEnum.FullRing]);
            let ant = this._axialRotations[BoneTypeEnum.LongLeg].Proximal.Left[RingTypeEnum.OpenAnteriorRing] = {};
            ant[RingTypeEnum.FullRing] = a(60) * external;
            ant[RingTypeEnum.FullRing58Plus38] = a(60) * external;
            ant[RingTypeEnum.OpenAnteriorRing] = a(60) * external;
            ant[RingTypeEnum.OpenMedialRing] = a(60) * external;
            ant[RingTypeEnum.OpenPosteriorRing] = a(60) * external;
            ant[RingTypeEnum.FootPlate] = a(60) * external;
            let med = this._axialRotations[BoneTypeEnum.LongLeg].Proximal.Left[RingTypeEnum.OpenMedialRing] = {};
            med[RingTypeEnum.FullRing] = a(90) * external;
            med[RingTypeEnum.FullRing58Plus38] = a(90) * external;
            med[RingTypeEnum.OpenAnteriorRing] = a(90) * external;
            med[RingTypeEnum.OpenMedialRing] = a(90) * external;
            med[RingTypeEnum.OpenPosteriorRing] = a(90) * external;
            med[RingTypeEnum.FootPlate] = a(30) * internal;
            let post = this._axialRotations[BoneTypeEnum.LongLeg].Proximal.Left[RingTypeEnum.OpenPosteriorRing] = {};
            post[RingTypeEnum.FullRing] = 0;
            post[RingTypeEnum.FullRing58Plus38] = 0;
            post[RingTypeEnum.OpenAnteriorRing] = 0;
            post[RingTypeEnum.OpenMedialRing] = 0;
            post[RingTypeEnum.OpenPosteriorRing] = 0;
            post[RingTypeEnum.FootPlate] = 0;
            let footPlate = this._axialRotations[BoneTypeEnum.LongLeg].Proximal.Left[RingTypeEnum.FootPlate] = {};
            footPlate[RingTypeEnum.FullRing] = a(180) * internal;
            footPlate[RingTypeEnum.FullRing58Plus38] = a(180) * internal;
            footPlate[RingTypeEnum.OpenAnteriorRing] = a(180) * internal;
            footPlate[RingTypeEnum.OpenMedialRing] = a(180) * internal;
            footPlate[RingTypeEnum.OpenPosteriorRing] = a(180) * internal;
            footPlate[RingTypeEnum.FootPlate] = a(180) * internal;
            // #endregion
            //#region LONGLEG FEMUR TIBIA - PROXIMAL RIGHT
            fullRing = this._axialRotations[BoneTypeEnum.LongLeg].Proximal.Right[RingTypeEnum.FullRing] = {};
            fullRing[RingTypeEnum.FullRing] = 0;
            fullRing[RingTypeEnum.FullRing58Plus38] = 0;
            fullRing[RingTypeEnum.OpenAnteriorRing] = 0;
            fullRing[RingTypeEnum.OpenMedialRing] = a(90) * internal;
            fullRing[RingTypeEnum.OpenPosteriorRing] = a(60) * internal;
            fullRing[RingTypeEnum.FootPlate] = 0;
            this._axialRotations[BoneTypeEnum.LongLeg].Proximal.Right[RingTypeEnum.FullRing58Plus38] = Utils.clone(this._axialRotations[BoneTypeEnum.LongLeg].Proximal.Right[RingTypeEnum.FullRing]);
            ant = this._axialRotations[BoneTypeEnum.LongLeg].Proximal.Right[RingTypeEnum.OpenAnteriorRing] = {};
            ant[RingTypeEnum.FullRing] = a(60) * internal;
            ant[RingTypeEnum.FullRing58Plus38] = a(60) * internal;
            ant[RingTypeEnum.OpenAnteriorRing] = a(60) * internal;
            ant[RingTypeEnum.OpenMedialRing] = a(60) * internal;
            ant[RingTypeEnum.OpenPosteriorRing] = a(60) * internal;
            ant[RingTypeEnum.FootPlate] = a(60) * internal;
            med = this._axialRotations[BoneTypeEnum.LongLeg].Proximal.Right[RingTypeEnum.OpenMedialRing] = {};
            med[RingTypeEnum.FullRing] = a(30) * internal;
            med[RingTypeEnum.FullRing58Plus38] = a(30) * internal;
            med[RingTypeEnum.OpenAnteriorRing] = a(30) * internal;
            med[RingTypeEnum.OpenMedialRing] = a(30) * internal;
            med[RingTypeEnum.OpenPosteriorRing] = a(30) * internal;
            med[RingTypeEnum.FootPlate] = a(30) * internal;
            post = this._axialRotations[BoneTypeEnum.LongLeg].Proximal.Right[RingTypeEnum.OpenPosteriorRing] = {};
            post[RingTypeEnum.FullRing] = 0;
            post[RingTypeEnum.FullRing58Plus38] = 0;
            post[RingTypeEnum.OpenAnteriorRing] = 0;
            post[RingTypeEnum.OpenMedialRing] = 0;
            post[RingTypeEnum.OpenPosteriorRing] = 0;
            post[RingTypeEnum.FootPlate] = 0;
            footPlate = this._axialRotations[BoneTypeEnum.LongLeg].Proximal.Right[RingTypeEnum.FootPlate] = {};
            footPlate[RingTypeEnum.FullRing] = a(180) * external;
            footPlate[RingTypeEnum.FullRing58Plus38] = a(180) * external;
            footPlate[RingTypeEnum.OpenAnteriorRing] = a(180) * external;
            footPlate[RingTypeEnum.OpenMedialRing] = a(180) * external;
            footPlate[RingTypeEnum.OpenPosteriorRing] = a(180) * external;
            footPlate[RingTypeEnum.FootPlate] = a(180) * external;
            // #endregion
            //#region LONGLEG FEMUR TIBIA - DISTAL LEFT
            fullRing = this._axialRotations[BoneTypeEnum.LongLeg].Distal.Left[RingTypeEnum.FullRing] = {};
            fullRing[RingTypeEnum.FullRing] = 0;
            fullRing[RingTypeEnum.FullRing58Plus38] = 0;
            fullRing[RingTypeEnum.OpenAnteriorRing] = 0;
            fullRing[RingTypeEnum.OpenMedialRing] = a(30) * external;
            fullRing[RingTypeEnum.OpenPosteriorRing] = a(60) * external;
            fullRing[RingTypeEnum.FootPlate] = 0;
            this._axialRotations[BoneTypeEnum.LongLeg].Distal.Left[RingTypeEnum.FullRing58Plus38] = Utils.clone(this._axialRotations[BoneTypeEnum.LongLeg].Distal.Left[RingTypeEnum.FullRing]);
            ant = this._axialRotations[BoneTypeEnum.LongLeg].Distal.Left[RingTypeEnum.OpenAnteriorRing] = {};
            ant[RingTypeEnum.FullRing] = a(60) * external;
            ant[RingTypeEnum.FullRing58Plus38] = a(60) * external;
            ant[RingTypeEnum.OpenAnteriorRing] = 0;
            ant[RingTypeEnum.OpenMedialRing] = a(30) * external;
            ant[RingTypeEnum.OpenPosteriorRing] = a(60) * external;
            ant[RingTypeEnum.FootPlate] = 0;
            med = this._axialRotations[BoneTypeEnum.LongLeg].Distal.Left[RingTypeEnum.OpenMedialRing] = {};
            med[RingTypeEnum.FullRing] = a(90) * external;
            med[RingTypeEnum.FullRing58Plus38] = a(90) * external;
            med[RingTypeEnum.OpenAnteriorRing] = 0;
            med[RingTypeEnum.OpenMedialRing] = a(30) * external;
            med[RingTypeEnum.OpenPosteriorRing] = a(60) * external;
            med[RingTypeEnum.FootPlate] = 0;
            post = this._axialRotations[BoneTypeEnum.LongLeg].Distal.Left[RingTypeEnum.OpenPosteriorRing] = {};
            post[RingTypeEnum.FullRing] = 0;
            post[RingTypeEnum.FullRing58Plus38] = 0;
            post[RingTypeEnum.OpenAnteriorRing] = 0;
            post[RingTypeEnum.OpenMedialRing] = a(30) * external;
            post[RingTypeEnum.OpenPosteriorRing] = a(60) * external;
            post[RingTypeEnum.FootPlate] = 0;
            footPlate = this._axialRotations[BoneTypeEnum.LongLeg].Distal.Left[RingTypeEnum.FootPlate] = {};
            footPlate[RingTypeEnum.FullRing] = a(180) * internal;
            footPlate[RingTypeEnum.FullRing58Plus38] = a(180) * internal;
            footPlate[RingTypeEnum.OpenAnteriorRing] = a(120) * external;
            footPlate[RingTypeEnum.OpenMedialRing] = a(150) * external;
            footPlate[RingTypeEnum.OpenPosteriorRing] = a(180) * external;
            footPlate[RingTypeEnum.FootPlate] = a(180) * internal;
            // #endregion
            //#region LONGLEG FEMUR TIBIA - DISTAL RIGHT
            fullRing = this._axialRotations[BoneTypeEnum.LongLeg].Distal.Right[RingTypeEnum.FullRing] = {};
            fullRing[RingTypeEnum.FullRing] = 0;
            fullRing[RingTypeEnum.FullRing58Plus38] = 0;
            fullRing[RingTypeEnum.OpenAnteriorRing] = 0;
            fullRing[RingTypeEnum.OpenMedialRing] = a(90) * internal;
            fullRing[RingTypeEnum.OpenPosteriorRing] = a(60) * internal;
            fullRing[RingTypeEnum.FootPlate] = 0;
            this._axialRotations[BoneTypeEnum.LongLeg].Distal.Right[RingTypeEnum.FullRing58Plus38] = Utils.clone(this._axialRotations[BoneTypeEnum.LongLeg].Distal.Right[RingTypeEnum.FullRing]);
            ant = this._axialRotations[BoneTypeEnum.LongLeg].Distal.Right[RingTypeEnum.OpenAnteriorRing] = {};
            ant[RingTypeEnum.FullRing] = a(60) * internal;
            ant[RingTypeEnum.FullRing58Plus38] = a(60) * internal;
            ant[RingTypeEnum.OpenAnteriorRing] = 0;
            ant[RingTypeEnum.OpenMedialRing] = a(90) * internal;
            ant[RingTypeEnum.OpenPosteriorRing] = a(60) * internal;
            ant[RingTypeEnum.FootPlate] = 0;
            med = this._axialRotations[BoneTypeEnum.LongLeg].Distal.Right[RingTypeEnum.OpenMedialRing] = {};
            med[RingTypeEnum.FullRing] = a(30) * internal;
            med[RingTypeEnum.FullRing58Plus38] = a(30) * internal;
            med[RingTypeEnum.OpenAnteriorRing] = 0;
            med[RingTypeEnum.OpenMedialRing] = a(90) * internal;
            med[RingTypeEnum.OpenPosteriorRing] = a(60) * internal;
            med[RingTypeEnum.FootPlate] = 0;
            post = this._axialRotations[BoneTypeEnum.LongLeg].Distal.Right[RingTypeEnum.OpenPosteriorRing] = {};
            post[RingTypeEnum.FullRing] = 0;
            post[RingTypeEnum.FullRing58Plus38] = 0;
            post[RingTypeEnum.OpenAnteriorRing] = 0;
            post[RingTypeEnum.OpenMedialRing] = a(90) * internal;
            post[RingTypeEnum.OpenPosteriorRing] = a(60) * internal;
            post[RingTypeEnum.FootPlate] = 0;
            footPlate = this._axialRotations[BoneTypeEnum.LongLeg].Distal.Right[RingTypeEnum.FootPlate] = {};
            footPlate[RingTypeEnum.FullRing] = a(180) * external;
            footPlate[RingTypeEnum.FullRing58Plus38] = a(180) * external;
            footPlate[RingTypeEnum.OpenAnteriorRing] = a(120) * internal;
            footPlate[RingTypeEnum.OpenMedialRing] = a(150) * external;
            footPlate[RingTypeEnum.OpenPosteriorRing] = a(180) * internal;
            footPlate[RingTypeEnum.FootPlate] = a(180) * external;
            //#endregion
            this._axialRotations[BoneTypeEnum.Ankle] = Utils.clone(this._axialRotations[BoneTypeEnum.LongLeg]);
            this._axialRotations[BoneTypeEnum.Forefoot] = this._axialRotations[BoneTypeEnum.Ankle];
            this._axialRotations[BoneTypeEnum.Hindfoot] = this._axialRotations[BoneTypeEnum.Ankle];
            this._axialRotations[BoneTypeEnum.Ankle].Proximal.Left[RingTypeEnum.FootPlate][RingTypeEnum.FootPlate] = 0;
            this._axialRotations[BoneTypeEnum.Ankle].Proximal.Right[RingTypeEnum.FootPlate][RingTypeEnum.FootPlate] = 0;
            this._axialRotations[BoneTypeEnum.Ankle].Distal.Left[RingTypeEnum.FootPlate][RingTypeEnum.FootPlate] = 0;
            this._axialRotations[BoneTypeEnum.Ankle].Distal.Right[RingTypeEnum.FootPlate][RingTypeEnum.FootPlate] = 0;
        }
        return this._axialRotations;
    }
}
//# sourceMappingURL=AxialRotations.js.map